import React, { useState, useCallback } from 'react';
import { Button, Space, Modal, Form, Input, Select, Radio, message } from 'antd';
import api from '../../services/api';
import { WF_ADD_SIGN_OPTIONS, WF_RETURN_MODES } from '../../config/workflowConstants';

const DEFAULT_ACTIONS = {
  enableTransfer: true,
  enableAddSign: true,
  enableReturn: true,
  allowPreAddSign: true,
  allowPostAddSign: true
};

function ApprovalActions({
  taskId,
  processId,
  apiMode = 'approval-center',
  approvalActions,
  returnSteps = [],
  loading: externalLoading,
  onSuccess
}) {
  const actions = approvalActions
    ? { ...DEFAULT_ACTIONS, ...approvalActions }
    : {
      enableTransfer: false,
      enableAddSign: false,
      enableReturn: false,
      allowPreAddSign: false,
      allowPostAddSign: false
    };
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [form] = Form.useForm();

  const busy = loading || externalLoading;

  const searchUsers = useCallback(async (keyword) => {
    try {
      setUserLoading(true);
      const res = await api.get('/approval-center/user-options', {
        params: keyword ? { keyword } : {}
      });
      if (res.data.success) {
        setUserOptions(res.data.data.map((u) => ({ value: u.id, label: u.label })));
      }
    } catch {
      /* ignore */
    } finally {
      setUserLoading(false);
    }
  }, []);

  const openModal = (action) => {
    setModalAction(action);
    form.resetFields();
    if (action === 'transfer' || action === 'add_sign') {
      searchUsers('');
    }
    if (action === 'add_sign') {
      const defaultMode = actions.allowPreAddSign ? 'pre' : 'post';
      form.setFieldsValue({ add_sign_mode: defaultMode });
    }
    if (action === 'return') {
      form.setFieldsValue({ return_mode: 'sequential' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalAction(null);
    form.resetFields();
  };

  const submitAction = async () => {
    const values = await form.validateFields();
    const payload = {
      action: modalAction,
      comment: values.comment || undefined
    };

    if (modalAction === 'transfer') {
      payload.target_user_id = values.target_user_id;
    }
    if (modalAction === 'add_sign') {
      payload.add_sign_mode = values.add_sign_mode;
      payload.add_sign_user_ids = values.add_sign_user_ids;
    }
    if (modalAction === 'return') {
      payload.return_step_index = values.return_step_index;
      payload.return_mode = values.return_mode || 'sequential';
    }

    try {
      setLoading(true);
      let res;
      if (apiMode === 'approval-center') {
        if (!taskId) {
          message.error('未找到待办任务');
          return;
        }
        res = await api.post(`/approval-center/tasks/${taskId}/approve`, payload);
      } else if (apiMode === 'expense') {
        res = await api.post(`/expense/claims/${processId}/approve`, payload);
      } else {
        res = await api.post(`/hr/processes/${processId}/approve`, payload);
      }
      message.success(res.data.message || '操作成功');
      closeModal();
      onSuccess?.();
    } catch (e) {
      message.error(e.response?.data?.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const modalTitles = {
    approve: '同意',
    reject: '驳回',
    transfer: '转交',
    add_sign: '加签',
    return: '退回'
  };

  const addSignOptions = WF_ADD_SIGN_OPTIONS.filter((opt) => (
    (opt.value === 'pre' && actions.allowPreAddSign)
    || (opt.value === 'post' && actions.allowPostAddSign)
  ));

  return (
    <div className="process-detail-actions">
      <Space wrap>
        <Button type="primary" loading={busy} onClick={() => openModal('approve')}>
          同意
        </Button>
        <Button danger loading={busy} onClick={() => openModal('reject')}>
          驳回
        </Button>
        {actions.enableTransfer && (
          <Button loading={busy} onClick={() => openModal('transfer')}>
            转交
          </Button>
        )}
        {actions.enableAddSign && addSignOptions.length > 0 && (
          <Button loading={busy} onClick={() => openModal('add_sign')}>
            加签
          </Button>
        )}
        {actions.enableReturn && returnSteps.length > 0 && (
          <Button loading={busy} onClick={() => openModal('return')}>
            退回
          </Button>
        )}
      </Space>

      <Modal
        title={modalTitles[modalAction] || '审批操作'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={submitAction}
        confirmLoading={busy}
        okText="确定"
        cancelText="取消"
        okButtonProps={{ danger: modalAction === 'reject' }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {modalAction === 'transfer' && (
            <Form.Item
              name="target_user_id"
              label="转交给"
              rules={[{ required: true, message: '请选择转交对象' }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder="搜索成员"
                options={userOptions}
                loading={userLoading}
                onSearch={searchUsers}
                onFocus={() => searchUsers('')}
              />
            </Form.Item>
          )}

          {modalAction === 'add_sign' && (
            <>
              <Form.Item
                name="add_sign_mode"
                label="加签方式"
                rules={[{ required: true, message: '请选择加签方式' }]}
              >
                <Radio.Group options={addSignOptions} />
              </Form.Item>
              <Form.Item
                name="add_sign_user_ids"
                label="加签人员"
                rules={[{ required: true, message: '请选择加签人员' }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  filterOption={false}
                  placeholder="搜索成员"
                  options={userOptions}
                  loading={userLoading}
                  onSearch={searchUsers}
                  onFocus={() => searchUsers('')}
                />
              </Form.Item>
            </>
          )}

          {modalAction === 'return' && (
            <>
              <Form.Item
                name="return_step_index"
                label="退回到"
                rules={[{ required: true, message: '请选择退回节点' }]}
              >
                <Select
                  placeholder="选择退回对象"
                  options={returnSteps.map((s) => ({
                    value: s.step_index,
                    label: s.label
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="return_mode"
                label="后续流转方式"
                rules={[{ required: true, message: '请选择流转方式' }]}
                initialValue="sequential"
              >
                <Radio.Group options={WF_RETURN_MODES} />
              </Form.Item>
            </>
          )}

          <Form.Item name="comment" label="审批意见">
            <Input.TextArea rows={3} placeholder="选填" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ApprovalActions;
